import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import ImageWithFallback from './ImageWithFallback';
import { getProfileUrl } from '../utils/urls';
import { getUserMe, PROFILE_UPDATED_EVENT, UserProfileDto } from '../utils/secureFunctions';

const LeftNavSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userPhoto, setUserPhoto] = useState('');
  const [userName, setUserName] = useState('');
  const [username, setUsername] = useState<string | null>(null);
  // removed unused isAdmin state (admin access via profile menu)

  useEffect(() => {
    const loadUser = async () => {
      if (!auth.currentUser) return;
      setUserPhoto(auth.currentUser.photoURL || '');
      setUserName(auth.currentUser.displayName || 'User');
      try {
        const res = await getUserMe();
        const data = res.profile as any;
        setUserPhoto(data.photoURL || auth.currentUser.photoURL || '');
        setUserName(data.name || auth.currentUser.displayName || 'User');
        setUsername((data.username || '').toString() || null);
      } catch {}
    };
    loadUser();
    const onProfileUpdated = (e: Event) => {
      const p = (e as CustomEvent<UserProfileDto>).detail;
      if (!p) return;
      if ('photoURL' in p) setUserPhoto(p.photoURL || '');
      if (p.name) setUserName(p.name);
      if ('username' in p) setUsername(p.username || null);
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { icon: '🏠', label: 'Home', path: '/home' },
    { icon: '🎬', label: 'Pulse', path: '/pulse' },
    { icon: '🙏', label: 'Testimonies', path: '/testimony' },
    { icon: '📖', label: 'Stories', path: '/stories' },
    { icon: '🔴', label: 'Live', path: '/live' },
  { icon: '👥', label: 'Groups', path: '/groups' },
  { icon: '💬', label: 'Messages', path: '/messages' },
    { icon: '🎵', label: 'Music Library', path: '/music-library' },
    { icon: '📌', label: 'Saved', path: '/saved' },
    { icon: '🔔', label: 'Notifications', path: '/notifications' },
  ];

  // Per branding, access Studio from profile dropdown only; no admin entry here

  return (
    <aside style={styles.sidebar}>
      <div style={styles.sidebarContent}>
        {/* User Profile Link */}
        <div
          style={{
            ...styles.navItem,
            backgroundColor: isActive(auth.currentUser ? getProfileUrl({ id: auth.currentUser.uid, username }) : '') ? 'var(--color-primary-50)' : 'transparent',
            fontWeight: isActive(auth.currentUser ? getProfileUrl({ id: auth.currentUser.uid, username }) : '') ? '600' : '400',
            color: 'var(--color-text-primary)',
          }}
          onClick={() => { if (auth.currentUser) navigate(getProfileUrl({ id: auth.currentUser.uid, username })); }}
        >
          <ImageWithFallback
            src={userPhoto}
            alt="Profile"
            style={styles.profileImage}
          />
          <span style={styles.navLabel}>{userName}</span>
        </div>

        <div style={styles.divider} />

        {/* Navigation Items */}
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            style={{
              ...styles.navItem,
              backgroundColor: isActive(item.path) ? 'var(--color-primary-50)' : 'transparent',
              fontWeight: isActive(item.path) ? '600' : '400',
              color: 'var(--color-text-primary)',
              textDecoration: 'none',
            }}
          >
            <span style={styles.navIcon}>{item.icon}</span>
            <span style={styles.navLabel}>{item.label}</span>
          </Link>
        ))}

        {/* Admin entry removed from sidebar intentionally */}
      </div>
    </aside>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  sidebar: {
    width: '280px',
    height: 'calc(100vh - 64px)',
    position: 'fixed',
    left: 0,
    top: 64,
    backgroundColor: 'var(--color-surface)',
    borderRight: '1px solid var(--color-border)',
    overflowY: 'auto',
    padding: 'var(--spacing-base) 0',
    zIndex: 100,
  },
  sidebarContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    padding: '10px var(--spacing-base)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    color: 'var(--color-text-primary)',
    margin: '0 var(--spacing-sm)',
  },
  navIcon: {
    fontSize: '20px',
    width: '24px',
    textAlign: 'center' as const,
  },
  navLabel: {
    fontSize: '15px',
  },
  profileImage: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    border: '2px solid var(--color-border-light)',
  },
  divider: {
    height: '1px',
    backgroundColor: 'var(--color-border)',
    margin: 'var(--spacing-sm) var(--spacing-base)',
  },
};

export default LeftNavSidebar;
